import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';

const formSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(100, "First name too long"),
  lastName: z.string().trim().min(1, "Last name is required").max(100, "Last name too long"),
  email: z.string().trim().email("Invalid email address").max(255, "Email too long"),
  phone: z.string().trim().max(20, "Phone number too long").optional(),
  location: z.string().trim().min(1, "Location is required").max(100, "Location too long"),
  message: z.string().trim().max(1800, "Message too long").optional()
});

interface MensRetreatBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MensRetreatBookingModal = ({ isOpen, onClose }: MensRetreatBookingModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate input
      const validationResult = formSchema.safeParse(formData);
      if (!validationResult.success) {
        const firstError = validationResult.error.errors[0];
        toast({
          title: "Validation Error",
          description: firstError.message,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Append location to message for database compatibility
      const fullMessage = `Men's Retreat Booking\nLocation: ${formData.location}\n${formData.message || ''}`.trim();

      // Use edge function with rate limiting instead of direct DB insert
      const { data: result, error } = await supabase.functions.invoke('submit-movement', {
        body: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          description: fullMessage,
          interestType: 'participant'
        }
      });

      if (error) {
        // Handle rate limiting error
        if (error.message?.includes('429') || error.message?.includes('Too many')) {
          toast({
            title: "Too many booking attempts",
            description: "Please try again in an hour. We've received your interest!",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }

      // Send email notification
      await supabase.functions.invoke('send-notification', {
        body: {
          type: 'mens_retreat_booking',
          data: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            location: formData.location,
            message: formData.message
          }
        }
      });

      toast({
        title: "Registration Submitted! 🐺",
        description: "Welcome to the pack! We'll contact you soon with retreat details and next steps.",
      });

      // Reset form and close modal
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        location: '',
        message: ''
      });
      onClose();

    } catch (error) {
      toast({
        title: "Something went wrong",
        description: "Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif font-light text-center mb-4">
            🐺 Reserve Your Place in the Pack
          </DialogTitle>
          <DialogDescription className="sr-only">
            Registration form for the Men's Wolfpack Retreat. November 14-16, 2025 in Beskid Wyspowy, Poland.
          </DialogDescription>
          <div className="text-center text-muted-foreground mb-6">
            <p className="text-lg mb-2">Wolfpack in the Mountains</p>
            <p>November 14–16, 2025 • Beskid Wyspowy, Poland</p>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                placeholder="Your first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                placeholder="Your last name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="your.email@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+48 123 456 789 (optional)"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Your Location *</Label>
            <Input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              required
              placeholder="City, Country"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Tell us about yourself</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="What draws you to this retreat? Any questions or special considerations? (Optional)"
              className="min-h-[100px]"
            />
          </div>

          <div className="bg-muted/50 p-4 rounded-lg">
            <p className="text-sm text-muted-foreground text-center">
              By submitting this form, you're expressing interest in the Men's Retreat. 
              We'll contact you with full details, pricing, and confirmation within 48 hours.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Joining the Pack..." : "Join the Pack 🐺"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};